import React, { useCallback, useEffect, useRef, useState } from "react"
// import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify"
import { useNavigate, useParams } from "react-router-dom"
import { request } from "../../services/request"
import { SensorData, SensorDataResponse } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import { FaChevronLeft } from "react-icons/fa"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"
import SensorImageModel from "./SensorImageModel"
import { LocationIcon, SensorIcon, ViewIcon } from "../../icons/webSvg"
import SensorUserModel from "./ShowUserModel"

const Icon: any = FaChevronLeft

const Sensors: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()

    const [plans, setPlans] = useState<SensorData[]>([])
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [totalItems, setTotalItems] = useState(100)
    const [search, setSearch] = useState("")
    const debounceText = useDebounce(search, 500)
    const [sort, setSort] = useState("DESC")
    const [sortCol, setSortCol] = useState("createdAt")
    const navigate = useNavigate()
    const param = useParams()

    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [floorImage, setFloorImage] = useState<string | null>(null)
    const [aspectRatio, setAspectRatio] = useState<any>(null)
    const [isModalOpen, setModalOpen] = React.useState(false)
    const [allSensors, setAllSensors] = React.useState<any>(null)
    const [isListModel, setIsListModel] = React.useState<boolean>(false)
    const [listModelData, setListModelData] = React.useState<any>([])
    const [modelLabel, setModelLabel] = React.useState<string>("")

    // Floor image 
    useEffect(() => {
        if (containerRef.current) {
            const updateSize = () => {
                if (!containerRef.current) return
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight,
                })
            }

            updateSize()
            const resizeObserver = new ResizeObserver(updateSize)
            resizeObserver.observe(containerRef.current)

            return () => resizeObserver.disconnect()
        }
    }, [floorImage])

    console.log({ listModelData })

    const getAspectRatio = useCallback(() => {
        if (!plans?.length) return 1;

        const smallestWidthSensor = plans.reduce((min, sensor) => {
            return parseFloat(sensor.width) < parseFloat(min.width) ? sensor : min;
        });

        const originalWidth = parseFloat(smallestWidthSensor.width);
        const originalHeight = parseFloat(smallestWidthSensor.height);

        return originalWidth / originalHeight;
    }, [plans]); // 👈 depends on plans

    useEffect(() => {
        setAspectRatio(getAspectRatio());
    }, [getAspectRatio]);

    const fetchSensors = useCallback(async () => {
        try {
            dispatch(changeloaderState(true))

            const params = {
                page: page,
                limit: rowsPerPage,
                search: debounceText,
                sort: sort,
                sortCol: sortCol,
            }
            const response = await request<SensorDataResponse>({
                url: `/adminCommon/getSensors/${param.id}`,
                method: "POST",
                data: params,
            })

            if (response.success) {
                setPlans(response.data)
                setFloorImage(response.otherData[0]?.image)
                setTotalItems(response.total)
            } else {
                toast.error("Failed to fetch Sensors: " + response.message)
            }
        } catch (error: any) {
            toast.error(`Error: ${error.message}`)
        } finally {
            dispatch(changeloaderState(false))
        }
    }, [page, rowsPerPage, debounceText, sort, sortCol, dispatch, param.id])

    useEffect(() => {
        fetchSensors()
    }, [page, rowsPerPage, debounceText, sort, fetchSensors])

    useEffect(() => {
        if (debounceText) {
            setPage(1) // Reset to first page on search
        }
    }, [debounceText])

    // const columns = [
    //     {
    //         name: "User Name",
    //         value: (plan: SensorData) =>
    //             !plan.createdlocationUserAccount.first_name && !plan.createdlocationUserAccount.last_name
    //                 ? "-"
    //                 : (plan.createdlocationUserAccount.first_name ? plan.createdlocationUserAccount.first_name : "") +
    //                 " " +
    //                 (plan.createdlocationUserAccount.last_name ? plan.createdlocationUserAccount.last_name : ""),

    //         id: "createdlocationUserAccount",
    //     },
    //     {
    //         name: "Sensor Name",
    //         value: (plan: SensorData) => plan?.name || "",
    //         id: "name",
    //     },
    //     {
    //         name: "Status",
    //         value: (plan: SensorData) => plan?.status === "1" ? "Active" : "Inactive",
    //         id: "status",
    //     },
    // ]

    const columns = [
        {
            name: "Device ID",
            value: (plan: SensorData) => plan?.sensor_id || "",
            id: "sensor_id",
        },
        {
            name: "Nickname",
            value: (plan: SensorData) =>
                plan?.name,
            id: "name",
        },
        {
            name: "Status",
            value: (plan: SensorData) => (
                <span className="d-flex align-items-center">
                    <span
                        className={`me-2 rounded-circle ${plan.status !== "0" ? "bg-success" : "bg-danger"}`}
                        style={{ width: "10px", height: "10px", display: "inline-block" }}
                    ></span>
                    <span>{plan.status === "0" ? "Offline" : "Active"}</span>
                </span>
            ),
            id: "status",
        },
        {
            name: "Last Contact",
            value: (plan: SensorData) =>
                plan?.testedAt ? new Date(plan?.testedAt).toLocaleString("en-CA", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false
                }).replace(",", "") : "-",  // remove comma after date
            id: "testedAt"
        },
        {
            name: "Model",
            value: (plan: SensorData) => "G3-SBS-R2D",
            id: "name",
        },
        {
            name: "Uptime",
            value: (plan: SensorData) => {
                if (!plan?.testedAt) return "-";

                const lastContact = new Date(plan?.testedAt);
                const now = new Date();
                let diffMs = now.getTime() - lastContact.getTime();

                if (diffMs < 0) return "—"; // future dates guard

                const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                diffMs -= days * (1000 * 60 * 60 * 24);

                const hours = Math.floor(diffMs / (1000 * 60 * 60));
                diffMs -= hours * (1000 * 60 * 60);

                const minutes = Math.floor(diffMs / (1000 * 60));

                if (days > 0) {
                    return `${days}d ${hours}h`;
                } else if (hours > 0) {
                    return `${hours}h ${minutes}m`;
                } else {
                    return `${minutes}m`;
                }
            },
            id: "uptime",
        },
    ]

    const action: TableAction[] = [
        {
            label: "",
            // onClick: (row: SensorData) => {
            //     navigate(`/location/device-details/${row.id}`)
            // },
            isRedirect: true,
            link: (row: SensorData) => `/location/device-details/${row.id}`,
            className: "table-btn-2",
            icon: (
                <ViewIcon />
            ),
        },
        {
            label: "",
            onClick: (row: SensorData) => {
                setAllSensors(
                    {
                        FloorplanSensor: plans.map((sensor: SensorData) => ({
                            dx: sensor.dx,
                            dy: sensor.dy,
                            width: sensor.width,
                            height: sensor.height,
                            isSelected: sensor.id === row.id,
                            sensorUserPositions: sensor.sensorUserPositions,
                            relatedSensors : sensor.relatedSensors,
                            name: sensor.name
                        })), planimage: floorImage
                    }
                );
                setModalOpen(true)
            },
            className: "table-btn-2",
            icon: (
                <LocationIcon />
            ),
        },
    ]

    const getBack = () => {
        navigate(-1)
    }

    const fetchAllSensors = async () => {
        try {
            const params = {
                page: page,
                search: debounceText,
                limit: 100000,
                sort: sort,
                sortCol: sortCol,
            }
            const response = await request<SensorDataResponse>({
                url: `/adminCommon/getSensors/${param.id}`,
                method: "POST",
                data: params,
            })

            if (response.success) {
                return response.data
            } else {
                toast.error("Failed to fetch bills: " + response.message)
                return []
            }
        } catch (error: any) {
            toast.error(`Error fetching all bills: ${error.message}`)
            return []
        }
    }

    const exportCSV = async () => {

        const allData = await fetchAllSensors()

        if (!allData.length) {
            toast.error("No data available to export")
            return
        }

        const headers = [
            "Sr. No.",
            "User Name",
            "Sensor Name",
            "Status",
        ]

        // ✅ Correct rows mapping for Sensor data
        const rows = allData.map((sensor, index) => [
            index + 1, // Sr. No.
            !sensor.createdlocationUserAccount.first_name && !sensor.createdlocationUserAccount.last_name
                ? "-"
                : `${sensor.createdlocationUserAccount.first_name || ""} ${sensor.createdlocationUserAccount.last_name || ""}`,
            sensor?.name || "",
            sensor?.status === "1" ? "Active" : "Inactive",
        ])

        donwloadCSV(headers, rows, "Sensors.csv")
    }

    const exportPDF = async () => {

        const allData = await fetchAllSensors()

        if (!allData.length) {
            toast.error("No data available to export")
            return
        }

        const headers = [
            "Sr. No.",
            "User Name",
            "Sensor Name",
            "Status",
        ]

        // ✅ Correct rows mapping for Sensor data
        const rows = allData.map((sensor, index) => [
            index + 1, // Sr. No.
            !sensor.createdlocationUserAccount.first_name && !sensor.createdlocationUserAccount.last_name
                ? "-"
                : `${sensor.createdlocationUserAccount.first_name || ""} ${sensor.createdlocationUserAccount.last_name || ""}`,
            sensor?.name || "",
            sensor?.status === "1" ? "Active" : "Inactive",
        ])

        // call utility
        downloadPDF(headers, rows, "Sensors")
    }

    return (
        <section className=' dashboard-section dashboard-item '>
            <ToastContainer position='top-right' autoClose={2000} theme='light' />
            <div className='container'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='d-flex gap-1 mb-2'>
                            <button onClick={getBack} className='d-flex align-items-center'>
                                <Icon size={20} />
                            </button>
                            <div className='dashboard-table-title-section w-100'>
                                <h2>Sensors</h2>
                                <div className='dashboard-table-title-right-side d-flex gap-2'>
                                    <PrimaryButton
                                        label='Export CSV'
                                        className='max-w-max-content'
                                        onClick={exportCSV}
                                    />
                                    <PrimaryButton
                                        label='Export PDF'
                                        className='max-w-max-content'
                                        onClick={exportPDF}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='col-12 col-md-8 col-lg-5 border border-2'>
                        {floorImage ? (
                            <div
                                ref={containerRef}
                                className="position-relative d-inline-block mx-auto"
                                style={{
                                    width: "100%",
                                    maxWidth: "800px",
                                    aspectRatio: aspectRatio,
                                    maxHeight: "80vh"
                                }}
                            >
                                {/* Floorplan Image */}
                                <img
                                    src={floorImage}
                                    alt="Preview"
                                    className="img-fluid d-block w-100 h-100"
                                    style={{ objectFit: "contain" }}
                                />

                                {/* Sensor Icons */}
                                {plans?.map((sensor, index) => {
                                    if (!sensor.dx || !sensor.dy) {
                                        return null
                                    }
                                    const iconWidth = 35
                                    const iconHeight = 35

                                    if (!containerSize.width || !containerSize.height) return null

                                    const originalWidth = parseFloat(sensor.width)
                                    const originalHeight = parseFloat(sensor.height)

                                    const dx = parseFloat(sensor.dx)
                                    const dy = parseFloat(sensor.dy)

                                    // Calculate position relative to container
                                    const mappedDx = (dx / originalWidth) * containerSize.width;
                                    const mappedDy = (dy / originalHeight) * containerSize.height;

                                    const policeCount = sensor?.sensorUserPositions?.filter(item => item?.is_police)?.length || 0;
                                    const userCount = sensor?.sensorUserPositions?.filter(item => !item?.is_police)?.length || 0;
                                    const sosCount = sensor?.relatedSensors?.filter(item => item?.status === "1")?.length || 0;


                                    return (
                                        <>
                                            <div
                                                key={index}
                                                style={{
                                                    position: "absolute",
                                                    left: `${mappedDx + (window.innerWidth < 600 ? 0 : 10)}px`,
                                                    top: `${mappedDy + (window.innerWidth < 600 ? 0 : 15)}px`,
                                                    width: `${iconWidth}px`,
                                                    height: `${iconHeight}px`,
                                                    zIndex: 12,
                                                    pointerEvents: "auto"
                                                }}
                                                // title={sensor.name || `Sensor ${index + 1}`}
                                                className="tooltip-custom"
                                                data-tooltip={sensor.name || `Sensor ${index + 1}`}
                                            >
                                                <SensorIcon />
                                            </div>
                                            {sosCount && sosCount > 0 ? (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: "absolute",
                                                        left: `${mappedDx - (window.innerWidth < 600 ? 35 : 25)}px`,
                                                        top: `${mappedDy + 15}px`,
                                                        width: `${iconWidth}px`,
                                                        height: `${iconHeight}px`,
                                                        zIndex: 10,
                                                        pointerEvents: "auto",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        setIsListModel(true);
                                                        setListModelData(sensor?.relatedSensors?.filter((item) => item?.status === "1"));
                                                        setModelLabel("SOS List");
                                                    }}
                                                >
                                                    <svg width="30" height="30" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="50" cy="50" r="50" fill="red" />
                                                        <text
                                                            x="50"
                                                            y="45"
                                                            textAnchor="middle"
                                                            dominant-baseline="central"
                                                            fontSize="50"
                                                            // fontWeight="bold"
                                                            fill="white"
                                                        >
                                                            {sosCount}
                                                        </text>
                                                    </svg>
                                                </div>
                                            ) : ""}

                                            {policeCount && policeCount > 0 ? <div
                                                key={index}
                                                style={{
                                                    position: "absolute",
                                                    left: `${mappedDx + 10}px`,
                                                    top: `${mappedDy - (window.innerWidth < 600 ? 35 : 25)}px`,
                                                    width: `${iconWidth}px`,
                                                    height: `${iconHeight}px`,
                                                    zIndex: 10,
                                                    pointerEvents: "auto",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => {
                                                    setIsListModel(true)
                                                    setListModelData(sensor?.sensorUserPositions?.filter(item => item.is_police))
                                                    setModelLabel("Police List")
                                                }}

                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120" width="30" height="30">
                                                    <path d="M50 0 L95 20 V60 C95 90 75 110 50 120 C25 110 5 90 5 60 V20 Z" fill="#00AEEF" stroke="black" stroke-width="1" />
                                                    <text
                                                        x="50"
                                                        y="50"
                                                        textAnchor="middle"
                                                        dominant-baseline="central"
                                                        fontSize="50"
                                                        // fontWeight="bold"
                                                        fill="white"
                                                    >
                                                        {policeCount}
                                                    </text>
                                                </svg>
                                            </div> : ""}
                                            {userCount && userCount > 0 ? <div
                                                key={index}
                                                style={{
                                                    position: "absolute",
                                                    left: `${mappedDx + (window.innerWidth < 600 ? 40 : 50)}px`,
                                                    top: `${mappedDy + 15}px`,
                                                    width: `${iconWidth}px`,
                                                    height: `${iconHeight}px`,
                                                    zIndex: 10,
                                                    pointerEvents: "auto",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => {
                                                    setIsListModel(true)
                                                    setListModelData(sensor?.sensorUserPositions?.filter(item => !item.is_police))
                                                    setModelLabel("User List")
                                                }}

                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="30" height="30">
                                                    <polygon points="50,0 100,50 50,100 0,50" fill="green" stroke="black" stroke-width="1" />
                                                    <text
                                                        x="50"
                                                        y="50"
                                                        textAnchor="middle"
                                                        fontSize="40"
                                                        dominant-baseline="central"
                                                        // fontWeight="bold"
                                                        fill="white"
                                                    >
                                                        {userCount}
                                                    </text>
                                                </svg>
                                            </div> : ""}
                                        </>
                                    )
                                })}
                            </div>
                        ) : (
                            <p>No image available</p>
                        )}
                    </div>

                    <div className='col-12 mt-4'>
                        <div className='table-container-main'>
                            <div className='table-details-section'></div>
                            <Table
                                rowData={plans}
                                columns={columns}
                                page={page}
                                setPage={setPage}
                                action={action}
                                rowsPerPage={rowsPerPage}
                                setRowsPerPage={setRowsPerPage}
                                totalItems={totalItems}
                                setSortColumn={setSortCol}
                                setSortOrder={setSort}
                                sortColumn={sortCol}
                                sortOrder={sort}
                                isSearch
                                search={search}
                                setSearch={setSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <SensorImageModel
                show={isModalOpen}
                onClose={() => {
                    setModalOpen(false)
                    setTimeout(() => {
                        setAllSensors(null)
                    }, 100)
                }}
                selectedImage={allSensors} />
            <SensorUserModel
                show={isListModel}
                onClose={() => {
                    setIsListModel(false)
                    setModelLabel("")
                    setListModelData([])
                }}
                users={listModelData}
                label={modelLabel}

            />
        </section>
    )
}

export default Sensors
